import { Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { RequireAuth } from './components/RequireAuth'
import { AssignToProjectProvider } from './components/ui/AssignToProjectProvider'
import { ToastProvider } from './components/ui/ToastProvider'
import { AuthProvider } from './lib/auth/auth-context'
import { CartCountProvider } from './lib/cart/cart-count-context'
import { RfqBundleProvider } from './lib/rfq/rfq-bundle-context'
import { RfqCountProvider } from './lib/rfq/rfq-count-context'
import { AccountSettingsPage } from './routes/AccountSettingsPage'
import { BillingPage } from './routes/BillingPage'
import { CreateCompanyPage } from './routes/CreateCompanyPage'
import { EmailVerificationPage } from './routes/EmailVerificationPage'
import { HomePage } from './routes/HomePage'
import { LoginPage } from './routes/LoginPage'
import { LoginRedirectPage } from './routes/LoginRedirectPage'
import { ManageUsersPage } from './routes/ManageUsersPage'
import { NotFoundPage } from './routes/NotFoundPage'
import { RequestToJoinPage } from './routes/RequestToJoinPage'
import { SignupPage } from './routes/SignupPage'
import { composedVerticals } from './verticals'
import { UploadFlowProvider } from './verticals/uploads/UploadFlowProvider'

export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        {/* UID-509 — above `<Routes>`, same tier as `ToastProvider`, so
        "Add these files to a project" survives the navigation to the
        staging page that now fires immediately on upload (matching the
        design's own sequencing) instead of unmounting with the page that
        triggered it. */}
        <AssignToProjectProvider>
          {/* UID-583 (ADR-0173 D2) — INSIDE `AssignToProjectProvider` and
          `ToastProvider` (it consumes both seams: `onNotice` toasts, and the
          assign-to-project prompt its callers pass through), and ABOVE
          `<Routes>`, so the presigned PUTs, thumbnail renders and
          BOM-hierarchy capture survive the navigation to the staging page
          that now fires as soon as the session exists. */}
          <UploadFlowProvider>
            <AuthProvider>
              <CartCountProvider>
                <RfqCountProvider>
                  <RfqBundleProvider>
                    <Routes>
                      {/* Outside the shell: the OIDC redirect hand-off has no nav/footer to
              render, it navigates away almost immediately (F0.2). F0.3 moves
              it from "/login" to "/login/sso": "/login" is now the credential
              email/password screen (LoginPage), and this SSO redirect is an
              explicit manual choice from there rather than the only login
              entry — see LoginPage's SCOPE NOTE for why (no domain-lookup
              endpoint exists yet to detect SSO automatically). */}
                      <Route path="/login/sso" element={<LoginRedirectPage />} />
                      {/* UID-27 (screens.md §1 "Login"): the split-panel login screen is
              "the entry gate before the app shell renders at all" — it never
              shows the authenticated sidebar/top-bar chrome, so it's mounted
              OUTSIDE <AppShell> (alongside the SSO hand-off above) rather than
              as a nested layout child, unlike every other pre-UID-27 auth/
              onboarding route below. */}
                      <Route path="/login" element={<LoginPage />} />
                      {/* Signup uses the same standalone split-panel design as login
                    (UID-301) — mounted OUTSIDE <AppShell> so it never shows the
                    authenticated sidebar/top-bar chrome. */}
                      <Route path="/signup" element={<SignupPage />} />
                      <Route element={<AppShell />}>
                        {/* Public onboarding funnel — reachable without a full session
                      (the account-less `onboarding_required` wizard, and email
                      verification from a link), so they stay OUTSIDE the auth
                      gate below. Everything else requires login. */}
                        <Route path="/onboarding/company" element={<CreateCompanyPage />} />
                        <Route path="/onboarding/join" element={<RequestToJoinPage />} />
                        <Route path="/verify-email" element={<EmailVerificationPage />} />
                        {/* UID-301: EVERY other route is behind `RequireAuth` — an
                      unauthenticated visitor (including after logout, and on an
                      unknown URL) is redirected to /login. Vertical screens
                      (UID-9) mount here via the registry; a vertical adds a
                      screen by editing ONLY its registry entry, never this file.
                      /admin/users (F0.6) and /account/billing (F0.8) are
                      post-auth app pages, so they live behind the gate too.
                      /account/settings (UID-343) is the account-menu dropdown's
                      "Account settings" destination, and also now the only nav
                      entry point to Billing/Manage users. */}
                        <Route element={<RequireAuth />}>
                          <Route index element={<HomePage />} />
                          <Route path="/admin/users" element={<ManageUsersPage />} />
                          <Route path="/account/billing" element={<BillingPage />} />
                          <Route path="/account/settings" element={<AccountSettingsPage />} />
                          {composedVerticals.routes.map((route) => (
                            <Route key={route.path} path={route.path} element={route.element} />
                          ))}
                          <Route path="*" element={<NotFoundPage />} />
                        </Route>
                      </Route>
                    </Routes>
                  </RfqBundleProvider>
                </RfqCountProvider>
              </CartCountProvider>
            </AuthProvider>
          </UploadFlowProvider>
        </AssignToProjectProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}
